/**
 * Integração do widget de chat do Bitrix24 (loader já injetado em public/index.html)
 * com os dados do usuário logado.
 *
 * O Bitrix dispara o evento global `onBitrixLiveChat` assim que o widget termina
 * de carregar, entregando a instância do widget em `event.detail.widget`. A partir
 * dela dá pra:
 *  - `setUserRegisterData` -> identifica o contato (nome/e-mail) no CRM
 *  - `setCustomData`       -> grid extra visível pro atendente (telefone/empresa)
 *
 * O launcher flutuante padrão é escondido via CSS (ver .b24-widget-button-inner-container
 * em App.vue), e o ícone de suporte do header abre o chat simulando um clique
 * no botão real do widget (`openBitrixChat`) - o Bitrix não expõe um método de
 * API documentado pra abrir o painel, o clique é o próprio mecanismo nativo que
 * ele usa internamente pra abrir/fechar. `.click()` funciona mesmo com o botão
 * escondido via `display: none`, já que não depende dele estar visível.
 *
 * Como o carregamento do script do Bitrix é assíncrono, os dados do usuário podem
 * ficar prontos antes ou depois do widget - por isso guardamos os dois lados e
 * aplicamos assim que ambos estiverem disponíveis. O mesmo vale para um pedido de
 * abertura do chat feito antes do widget carregar.
 */

let widgetInstance = null;
let pendingUserData = null;
let pendingOpen = false;

// Seletores do botão real do widget, confirmados inspecionando o DOM. Mantidos
// em ordem de preferência (o data-attribute tende a ser mais estável entre
// versões do widget do que a classe BEM).
const WIDGET_BUTTON_SELECTORS = [
    "[data-b24-crm-button-block-button]",
    ".b24-widget-button-inner-container",
];

function clickWidgetButton() {
    const button = WIDGET_BUTTON_SELECTORS.map((selector) =>
        document.querySelector(selector),
    ).find(Boolean);
    if (!button) return false;

    button.click();
    return true;
}

// Segredo fixo só pra reduzir colisão do hash entre contatos (não é usado pelo
// Bitrix como credencial de autenticação, é só um valor que nós definimos).
const HASH_SECRET = "club-cotacao-bitrix";

// Implementação MD5 (domínio público, baseada no algoritmo de referência RFC 1321).
function md5(input) {
    function rotateLeft(x, c) {
        return (x << c) | (x >>> (32 - c));
    }
    function addUnsigned(x, y) {
        const x4 = x & 0x40000000;
        const y4 = y & 0x40000000;
        const x8 = x & 0x80000000;
        const y8 = y & 0x80000000;
        const result = (x & 0x3fffffff) + (y & 0x3fffffff);
        if (x4 & y4) return result ^ 0x80000000 ^ x8 ^ y8;
        if (x4 | y4) {
            if (result & 0x40000000) return result ^ 0xc0000000 ^ x8 ^ y8;
            return result ^ 0x40000000 ^ x8 ^ y8;
        }
        return result ^ x8 ^ y8;
    }
    function f(x, y, z) {
        return (x & y) | (~x & z);
    }
    function g(x, y, z) {
        return (x & z) | (y & ~z);
    }
    function h(x, y, z) {
        return x ^ y ^ z;
    }
    function i(x, y, z) {
        return y ^ (x | ~z);
    }
    function ff(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(f(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function gg(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(g(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function hh(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(h(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function ii(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(i(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function convertToWordArray(str) {
        let wordCount;
        const messageLength = str.length;
        const numberOfWordsTemp1 = messageLength + 8;
        const numberOfWordsTemp2 =
            (numberOfWordsTemp1 - (numberOfWordsTemp1 % 64)) / 64;
        const numberOfWords = (numberOfWordsTemp2 + 1) * 16;
        const wordArray = new Array(numberOfWords - 1);
        let bytePosition = 0;
        let byteCount = 0;
        while (byteCount < messageLength) {
            wordCount = (byteCount - (byteCount % 4)) / 4;
            bytePosition = (byteCount % 4) * 8;
            wordArray[wordCount] =
                wordArray[wordCount] |
                (str.charCodeAt(byteCount) << bytePosition);
            byteCount++;
        }
        wordCount = (byteCount - (byteCount % 4)) / 4;
        bytePosition = (byteCount % 4) * 8;
        wordArray[wordCount] = wordArray[wordCount] | (0x80 << bytePosition);
        wordArray[numberOfWords - 2] = messageLength << 3;
        wordArray[numberOfWords - 1] = messageLength >>> 29;
        return wordArray;
    }
    function wordToHex(value) {
        let hex = "";
        for (let count = 0; count <= 3; count++) {
            const byte = (value >>> (count * 8)) & 255;
            hex += ("0" + byte.toString(16)).slice(-2);
        }
        return hex;
    }

    const utf8 = unescape(encodeURIComponent(input));
    const x = convertToWordArray(utf8);
    let a = 0x67452301;
    let b = 0xefcdab89;
    let c = 0x98badcfe;
    let d = 0x10325476;

    const S = [
        [7, 12, 17, 22],
        [5, 9, 14, 20],
        [4, 11, 16, 23],
        [6, 10, 15, 21],
    ];

    for (let k = 0; k < x.length; k += 16) {
        const aa = a,
            bb = b,
            cc = c,
            dd = d;

        a = ff(a, b, c, d, x[k + 0], S[0][0], 0xd76aa478);
        d = ff(d, a, b, c, x[k + 1], S[0][1], 0xe8c7b756);
        c = ff(c, d, a, b, x[k + 2], S[0][2], 0x242070db);
        b = ff(b, c, d, a, x[k + 3], S[0][3], 0xc1bdceee);
        a = ff(a, b, c, d, x[k + 4], S[0][0], 0xf57c0faf);
        d = ff(d, a, b, c, x[k + 5], S[0][1], 0x4787c62a);
        c = ff(c, d, a, b, x[k + 6], S[0][2], 0xa8304613);
        b = ff(b, c, d, a, x[k + 7], S[0][3], 0xfd469501);
        a = ff(a, b, c, d, x[k + 8], S[0][0], 0x698098d8);
        d = ff(d, a, b, c, x[k + 9], S[0][1], 0x8b44f7af);
        c = ff(c, d, a, b, x[k + 10], S[0][2], 0xffff5bb1);
        b = ff(b, c, d, a, x[k + 11], S[0][3], 0x895cd7be);
        a = ff(a, b, c, d, x[k + 12], S[0][0], 0x6b901122);
        d = ff(d, a, b, c, x[k + 13], S[0][1], 0xfd987193);
        c = ff(c, d, a, b, x[k + 14], S[0][2], 0xa679438e);
        b = ff(b, c, d, a, x[k + 15], S[0][3], 0x49b40821);

        a = gg(a, b, c, d, x[k + 1], S[1][0], 0xf61e2562);
        d = gg(d, a, b, c, x[k + 6], S[1][1], 0xc040b340);
        c = gg(c, d, a, b, x[k + 11], S[1][2], 0x265e5a51);
        b = gg(b, c, d, a, x[k + 0], S[1][3], 0xe9b6c7aa);
        a = gg(a, b, c, d, x[k + 5], S[1][0], 0xd62f105d);
        d = gg(d, a, b, c, x[k + 10], S[1][1], 0x02441453);
        c = gg(c, d, a, b, x[k + 15], S[1][2], 0xd8a1e681);
        b = gg(b, c, d, a, x[k + 4], S[1][3], 0xe7d3fbc8);
        a = gg(a, b, c, d, x[k + 9], S[1][0], 0x21e1cde6);
        d = gg(d, a, b, c, x[k + 14], S[1][1], 0xc33707d6);
        c = gg(c, d, a, b, x[k + 3], S[1][2], 0xf4d50d87);
        b = gg(b, c, d, a, x[k + 8], S[1][3], 0x455a14ed);
        a = gg(a, b, c, d, x[k + 13], S[1][0], 0xa9e3e905);
        d = gg(d, a, b, c, x[k + 2], S[1][1], 0xfcefa3f8);
        c = gg(c, d, a, b, x[k + 7], S[1][2], 0x676f02d9);
        b = gg(b, c, d, a, x[k + 12], S[1][3], 0x8d2a4c8a);

        a = hh(a, b, c, d, x[k + 5], S[2][0], 0xfffa3942);
        d = hh(d, a, b, c, x[k + 8], S[2][1], 0x8771f681);
        c = hh(c, d, a, b, x[k + 11], S[2][2], 0x6d9d6122);
        b = hh(b, c, d, a, x[k + 14], S[2][3], 0xfde5380c);
        a = hh(a, b, c, d, x[k + 1], S[2][0], 0xa4beea44);
        d = hh(d, a, b, c, x[k + 4], S[2][1], 0x4bdecfa9);
        c = hh(c, d, a, b, x[k + 7], S[2][2], 0xf6bb4b60);
        b = hh(b, c, d, a, x[k + 10], S[2][3], 0xbebfbc70);
        a = hh(a, b, c, d, x[k + 13], S[2][0], 0x289b7ec6);
        d = hh(d, a, b, c, x[k + 0], S[2][1], 0xeaa127fa);
        c = hh(c, d, a, b, x[k + 3], S[2][2], 0xd4ef3085);
        b = hh(b, c, d, a, x[k + 6], S[2][3], 0x04881d05);
        a = hh(a, b, c, d, x[k + 9], S[2][0], 0xd9d4d039);
        d = hh(d, a, b, c, x[k + 12], S[2][1], 0xe6db99e5);
        c = hh(c, d, a, b, x[k + 15], S[2][2], 0x1fa27cf8);
        b = hh(b, c, d, a, x[k + 2], S[2][3], 0xc4ac5665);

        a = ii(a, b, c, d, x[k + 0], S[3][0], 0xf4292244);
        d = ii(d, a, b, c, x[k + 7], S[3][1], 0x432aff97);
        c = ii(c, d, a, b, x[k + 14], S[3][2], 0xab9423a7);
        b = ii(b, c, d, a, x[k + 5], S[3][3], 0xfc93a039);
        a = ii(a, b, c, d, x[k + 12], S[3][0], 0x655b59c3);
        d = ii(d, a, b, c, x[k + 3], S[3][1], 0x8f0ccc92);
        c = ii(c, d, a, b, x[k + 10], S[3][2], 0xffeff47d);
        b = ii(b, c, d, a, x[k + 1], S[3][3], 0x85845dd1);
        a = ii(a, b, c, d, x[k + 8], S[3][0], 0x6fa87e4f);
        d = ii(d, a, b, c, x[k + 15], S[3][1], 0xfe2ce6e0);
        c = ii(c, d, a, b, x[k + 6], S[3][2], 0xa3014314);
        b = ii(b, c, d, a, x[k + 13], S[3][3], 0x4e0811a1);
        a = ii(a, b, c, d, x[k + 4], S[3][0], 0xf7537e82);
        d = ii(d, a, b, c, x[k + 11], S[3][1], 0xbd3af235);
        c = ii(c, d, a, b, x[k + 2], S[3][2], 0x2ad7d2bb);
        b = ii(b, c, d, a, x[k + 9], S[3][3], 0xeb86d391);

        a = addUnsigned(a, aa);
        b = addUnsigned(b, bb);
        c = addUnsigned(c, cc);
        d = addUnsigned(d, dd);
    }

    return (
        wordToHex(a) +
        wordToHex(b) +
        wordToHex(c) +
        wordToHex(d)
    ).toLowerCase();
}

function buildHash(userId) {
    return md5(`${userId}_${window.location.hostname}_${HASH_SECRET}`);
}

function applyUserData({
    userId,
    name,
    email,
    phone,
    company,
    cliId,
    grupo,
    dealId,
    dealUrl,
}) {
    if (!widgetInstance) return;

    widgetInstance.setUserRegisterData({
        hash: buildHash(userId),
        name: name || "",
        email: email || "",
    });

    const grid = [
        email && { NAME: "E-mail", VALUE: email, DISPLAY: "LINE" },
        phone && { NAME: "Telefone", VALUE: phone, DISPLAY: "LINE" },
        company && { NAME: "Empresa", VALUE: company, DISPLAY: "LINE" },
        grupo && { NAME: "Grupo", VALUE: grupo, DISPLAY: "LINE" },
        cliId && { NAME: "Código do cliente", VALUE: cliId, DISPLAY: "LINE" },
        userId && { NAME: "Código do usuário", VALUE: userId, DISPLAY: "LINE" },
        dealId && {
            NAME: "Negócio (Deal)",
            VALUE: dealUrl ? `[url=${dealUrl}]Abrir Deal #${dealId}[/url]` : `#${dealId}`,
            DISPLAY: "LINE",
        },
    ].filter(Boolean);

    if (grid.length) {
        widgetInstance.setCustomData([
            { USER: { NAME: name || "" } },
            { GRID: grid },
        ]);
    }
}

window.addEventListener("onBitrixLiveChat", (event) => {
    widgetInstance = event.detail.widget;

    if (pendingUserData) applyUserData(pendingUserData);
    if (pendingOpen) {
        pendingOpen = false;
        clickWidgetButton();
    }
});

/**
 * @param {{ userId: string|number, name: string, email: string, phone: string, company: string, cliId: string|number, grupo: string, dealId?: string|number, dealUrl?: string }} userData
 */
export function setBitrixUserData(userData) {
    pendingUserData = userData;
    if (widgetInstance) applyUserData(userData);
}

/**
 * Abre o painel do chat do Bitrix a partir do ícone de suporte do header,
 * simulando um clique no botão real do widget (ver `clickWidgetButton`).
 * Se o botão ainda não tiver sido renderizado, fica pendente e o clique é
 * repetido assim que o evento `onBitrixLiveChat` chegar.
 */
export function openBitrixChat() {
    if (!clickWidgetButton()) pendingOpen = true;
}
